import { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { requestHeader } from "../../../../../others/header";
import axios from "axios";
import { message } from "antd";

class CBAPIError extends Error {
  constructor(message, code, response) {
    super(message);
    this.name = 'CBAPIError';
    this.code = code;
    this.response = response;
  }
}

// Error messages mapping
const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error occurred. Please check your connection.',
  INVALID_RESPONSE: 'Invalid response received from server.',
  PARSE_ERROR: 'Error parsing server response.',
  EMPTY_RESPONSE: 'No results found.',
  RATE_LIMIT: 'Rate limit exceeded. Please try again later.',
  UNAUTHORIZED: 'Unauthorized access. Please check your credentials.',
  SERVER_ERROR: 'Server error occurred. Please try again later.',
  DEFAULT: 'An unexpected error occurred.',
  400: 'Invalid request parameters.',
  401: 'Unauthorized access. Please check your credentials.',
  403: 'Access forbidden. Please check your permissions.',
  404: 'Resource not found.',
  429: 'Too many requests. Please try again later.',
  500: 'Internal server error.',
  503: 'Service temporarily unavailable.',
};

export const useSearchFunction = (apiURL) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  const requestCount = useRef(0);
  const lastRequestTime = useRef(0);
  
  // Add a ref to track active requests
  const activeRequests = useRef(new Map());

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleError = useCallback((error) => {
    console.error('Search error:', error);

    let errorMessage = ERROR_MESSAGES.DEFAULT;
    let shouldShowUser = true;
    let errorCode = null;

    if (axios.isCancel(error)) {
      shouldShowUser = false;
    } else if (error instanceof CBAPIError) {
      errorMessage = error.message;
      errorCode = error.code;
    } else if (error.response) {
      errorCode = error.response.status;
      errorMessage = error.response.data?.message || 
                    ERROR_MESSAGES[errorCode] ||
                    `Server error: ${errorCode}`;

      // Special handling for rate limiting
      if (errorCode === 429) {
        const retryAfter = error.response.headers['retry-after'];
        if (retryAfter) {
          errorMessage += ` Please wait ${retryAfter} seconds.`;
        }
      }
    } else if (error.request) {
      errorMessage = ERROR_MESSAGES.NETWORK_ERROR;
      errorCode = 'NETWORK_ERROR';
    }

    if (shouldShowUser) {
      message.error({
        content: errorMessage,
        key: `search-error-${errorCode}`,
        duration: 5
      });
    }

    return { results: [], total: 0, error: errorMessage };
  }, []);

  const validateAndParseResponse = useCallback((responseData, adapter) => {
    if (!responseData) {
      throw new CBAPIError(ERROR_MESSAGES.EMPTY_RESPONSE, 'EMPTY_RESPONSE');
    }

    try {
      // Handle different response formats
      let dataToProcess = responseData;
      
      // Special handling for CareerBuilder API response format
      if (responseData.data?.CareerBuilder?.Resumes) {
        dataToProcess = responseData.data.CareerBuilder.Resumes;
      } else if (responseData.CareerBuilder?.Resumes) {
        dataToProcess = responseData.CareerBuilder.Resumes;
      } else if (responseData.data?.data) {
        dataToProcess = responseData.data.data;
      } else if (responseData.results) {
        dataToProcess = responseData;
      }


      // Validate data structure
      if (!dataToProcess || (typeof dataToProcess !== 'object' && !Array.isArray(dataToProcess))) {
        throw new CBAPIError(ERROR_MESSAGES.INVALID_RESPONSE, 'INVALID_RESPONSE');
      }

      const parsedResults = adapter.parseResults(dataToProcess);

      // Validate parsed results
      if (!parsedResults) {
        throw new CBAPIError(ERROR_MESSAGES.PARSE_ERROR, 'PARSE_ERROR');
      }

      let normalizedResults;
      if (Array.isArray(parsedResults)) {
        normalizedResults = { 
          results: parsedResults, 
          total: parsedResults.length 
        };
      } else if (parsedResults.results) {
        normalizedResults = parsedResults;
      } else {
        normalizedResults = { 
          results: [parsedResults], 
          total: 1 
        };
      }

      if (!Array.isArray(normalizedResults.results)) {
        normalizedResults.results = [];
        normalizedResults.total = 0;
      }

      normalizedResults.results.forEach((result, index) => {
        if (!result.id) {
          console.warn(`Result at index ${index} missing required 'id' field:`, result);
        }
      });

      return normalizedResults;

    } catch (err) {
      console.error('Error parsing response:', err, responseData);
      throw new CBAPIError(
        err.message || ERROR_MESSAGES.PARSE_ERROR,
        'PARSE_ERROR',
        responseData
      );
    }
  }, []);

  const search = useCallback(async (params, adapter) => {
    if (!mounted.current) return null;
    
    const requestSignature = JSON.stringify(params);
    
    if (activeRequests.current.has(requestSignature)) {
      return activeRequests.current.get(requestSignature);
    }
    
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    
    // Create a promise that we can track
    const requestPromise = (async () => {
      try {
        // Validate adapter
        if (!adapter || typeof adapter.formatParams !== 'function') {
          throw new CBAPIError('Invalid adapter configuration', 'INVALID_ADAPTER');
        }

      
        
        // Rate limiting check
        const now = Date.now();
        if (now - lastRequestTime.current < 1000) { // Minimum 1 second between requests
          requestCount.current++;
          if (requestCount.current > 5) { // Max 5 requests per second
            throw new CBAPIError(ERROR_MESSAGES.RATE_LIMIT, 'RATE_LIMIT');
          }
        } else {
          requestCount.current = 1;
        }
        lastRequestTime.current = now;

        const formattedParams = adapter.formatParams(params);
 

        const response = await axios.get(
          `${apiURL}?${formattedParams}`,
          {
            ...requestHeader(),
            signal: controller.signal,
            timeout: 30000, // 30 second timeout
            validateStatus: status => status < 500, // Handle 4xx errors in catch block
          }
        );

        // Check if component is still mounted before updating state
        if (!mounted.current) return null;

        const results = validateAndParseResponse(response.data, adapter);
        
        // Check again if component is still mounted before updating state
        if (!mounted.current) return null;
        
        setData(results);
        return results;

      } catch (err) {
        // Check if component is still mounted before updating state
        if (!mounted.current) return null;
        
        const errorResult = handleError(err);
        setError(errorResult.error);
        return errorResult;

      } finally {
        // Check if component is still mounted before updating state
        if (mounted.current) {
          setLoading(false);
        }
        controller.abort();
        
        // Remove this request from active requests
        activeRequests.current.delete(requestSignature);
      }
    })();
    
    // Store the promise in our active requests map
    activeRequests.current.set(requestSignature, requestPromise);
    
    return requestPromise;
  }, [apiURL, validateAndParseResponse, handleError]);

  return useMemo(() => ({
    search,
    data,
    loading,
    error,
    clearError: () => setError(null)
  }), [search, data, loading, error]);
};